<template>
	<TeamingDialog
		:class-name="classes"
		:show-dialog="showDialog"
		v-on="bubble( `hide` )"
	>
		<template #heading>
			{{ translate( "heading" ) }}
		</template>

		<div
			class="new-team-dialog__processing-indicator"
			v-if="showProcessingIndicator"
		>
			<socket size="112px"></socket>
		</div>
		<div
			class="new-team-dialog__body"
			ref="dialogBody"
		>
			<div class="new-team-dialog__body-row new-team-dialog__team-name-row">
				<LabeledInputField
					:hint="translate( `teamNameFieldHint` )"
					:isValid="!newTeamDetails.name.validationErrors.length"
					:label="translate( `teamNameFieldLabel` )"
					:placeholder="translate( `teamNameFieldPlaceholder` )"
					:validationError="newTeamDetails.name.validationErrors[ 0 ]"
					:value="newTeamDetails.name.value"
					:focus="showDialog"
					@input="onTeamNameInput"
				/>
			</div>

			<div class="new-team-dialog__body-row">
				<div class="new-team-dialog__add-member-heading">
					{{ translate( "addMembersHeading" ) }}
				</div>
				<div class="new-team-dialog__add-member-row">
					<div class="new-team-dialog__add-member-input">
						<LabeledInputField
							:hint="translate( `memberBeingAddedFieldHint` )"
							:isValid="!memberBeingAddedValidationErrors.length"
							:label="translate( `memberBeingAddedFieldLabel` )"
							:placeholder="translate( `memberBeingAddedFieldPlaceholder` )"
							:validationError="memberBeingAddedValidationErrors[ 0 ]"
							:value="newTeamDetails.memberBeingAdded.value"
							@input="onAddMemberInput"
							@blur="onAddMemberInput"
							@enterKey="onAddMemberViaEnterKey"
						/>
					</div>
					<div class="new-team-dialog__add-member-button">
						<div class="new-team-dialog__add-member-btn-spacer">&nbsp;</div>
						<TeamingButton2
							:type="enums.TeamingButtonType.SECONDARY"
							size="MEDIUM"
							@click.stop="onAddMember"
							:disabled="memberAddDisabled"
						>
							{{ translate( "memberBeingAddedButton" ) }}
						</TeamingButton2>
					</div>
				</div>
			</div>

			<div class="new-team-dialog__body-row new-team-dialog__members-row">
				<NewTeamMemberRowHeader />
				<NewTeamMemberRow
					v-for="member in newTeamDetails.members.value"
					v-bind:key="member.email"
					:member="member"
					@roleChange="onRoleChange"
					@remove="onRemove"
				/>
			</div>

			<div class="new-team-dialog__footer">
				<TeamingButton2
					@click.stop="$emit( `submit` )"
				>
					{{ translate( "createButton" ) }}
				</TeamingButton2>
				<TeamingButton2
					:type="enums.TeamingButtonType.QUATERNARY"
					@click.stop="$emit( `cancel` )"
				>
					{{ translate( "cancelButton" ) }}
				</TeamingButton2>
			</div>
		</div>
	</TeamingDialog>
</template>

<script>
import {
	TeamMemberStatusType,
	TeamRoleType
} from "@teaming/common-enums";

import {
	EnumsMixin,
	EventsMixin,
	TranslateMixin,
	LabeledInputField,
	TeamingButton2,
	TeamingDialog
} from "@teaming/common-ui";

import NewTeamMemberRowHeader from "./NewTeamMemberRowHeader.vue";
import NewTeamMemberRow from "./NewTeamMemberRow.vue";

export default {
	name: "NewTeamDialog",
	teamingPackage: "common-team",
	mixins: [ TranslateMixin, EventsMixin, EnumsMixin, ],
	inject: [ "config", ],
	emits: [ "update", ],
	components: {
		TeamingDialog,
		LabeledInputField,
		NewTeamMemberRow,
		NewTeamMemberRowHeader,
		TeamingButton2,
	},
	// emits: [ "update", "hide", "submit", "cancel", ],
	props: {
		className: {
			type: String,
			default: "",
		},
		showDialog: {
			type: Boolean,
			required: true,
		},
		showProcessingIndicator: {
			type: Boolean,
			required: true,
		},
		newTeamDetails: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			newUserEmailHasBlurred: false,
		};
	},
	computed: {
		classes() {
			return `new-team-dialog ${ this.className }`.trim();
		},
		memberAddDisabled() {
			const memberBeingAddedHasValidationErrors = !!this.newTeamDetails.memberBeingAdded.validationErrors.length;
			const memberBeingAddedIsEmpty = !this.newTeamDetails.memberBeingAdded.value;
			return memberBeingAddedHasValidationErrors || memberBeingAddedIsEmpty;
		},
		memberBeingAddedValidationErrors() {
			const { newTeamDetails, } = this;

			// hide validation until the user has blurred out of the field
			if ( !this.newUserEmailHasBlurred ) {
				return [];
			}

			return newTeamDetails.memberBeingAdded.validationErrors;
		},
	},
	methods: {
		async onTeamNameInput( e ) {
			const { members, memberBeingAdded, } = this.newTeamDetails;
			await this.$nextTick();
			this.$emit( "update", {
				name: e.target.value,
				memberBeingAdded: memberBeingAdded.value,
				members: members.value,
				sourceFieldVisited: "name",
			} );
		},
		async onAddMemberInput( e ) {
			if ( !this.newUserEmailHasBlurred ) {
				this.newUserEmailHasBlurred = ( e.type === "blur" );
			}
			const { members, name, } = this.newTeamDetails;
			await this.$nextTick();
			this.$emit( "update", {
				name: name.value,
				memberBeingAdded: e.target.value,
				members: members.value,
				sourceFieldVisited: "memberBeingAdded",
			} );
		},
		onAddMemberViaEnterKey( e ) {
			if ( this.memberAddDisabled ) {
				this.onAddMemberInput( e );
			} else {
				this.onAddMember();
			}
		},
		async onAddMember() {
			const { members, name, memberBeingAdded, } = this.newTeamDetails;
			await this.$nextTick();
			this.$emit( "update", {
				name: name.value,
				memberBeingAdded: "",
				members: [
					...members.value,
					{
						email: memberBeingAdded.value,
						displayName: memberBeingAdded.value,
						status: TeamMemberStatusType.INVITED,
						role: TeamRoleType.MEMBER,
					},
				],
			} );

			this.newUserEmailHasBlurred = false;
		},
		async onRoleChange( member ) {
			const { members, name, memberBeingAdded, } = this.newTeamDetails;
			const _members = members.value.reduce( ( acc, m ) => {
				if ( m.email === member.email ) {
					m.role = member.role;
				}
				acc.push( m );
				return acc;
			}, [] );
			await this.$nextTick();
			this.$emit( "update", {
				name: name.value,
				memberBeingAdded: memberBeingAdded.value,
				members: _members,
			} );
		},
		async onRemove( member ) {
			const { members, name, memberBeingAdded, } = this.newTeamDetails;
			members.value = members.value.filter( x => x.email !== member.email );
			await this.$nextTick();
			this.$emit( "update", {
				name: name.value,
				memberBeingAdded: memberBeingAdded.value,
				members: members.value,
			} );
		},
	},
};
</script>

<style lang="scss" scoped>
$teamNameBorderColor: $color1-200;
$addMemberHeadingColor: $color2-500;

.new-team-dialog__processing-indicator {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	background: $default-loading-bg-color;
	display:flex;
	align-items:center;
	justify-content: center;
	opacity: .95;
}

.new-team-dialog__footer {
	width: 100%;
	display: flex;
	justify-content: flex-end;
	margin-top: 0.75rem;

	button {
		margin-left: .5rem;
	}
}

.new-team-dialog__body {
	width: 100%;

	// NOTE: min-height is necessary to give the
	// dropdown vertical clearance when open
	min-height: 100vh;

	@include for-size('sm') {
		min-height: 40vh;
	}

	@include for-size-and-smaller('sm') {
		padding: 0 0.25rem 0 0.25rem;
	}
}

.new-team-dialog__body-row {
	position: relative;
	margin-top: 1rem;

	&:first-of-type {
		margin-top: 0;
	}
}

.new-team-dialog__team-name-row {
	padding-bottom: 1rem;
	border-bottom: 0.0625rem solid $teamNameBorderColor;
}

.new-team-dialog__add-member-label {
	@include font-and-line-for(0);
	font-weight: 600;
	text-align:left;
	color: $labeled-input-field-label-font-color;
}

.new-team-dialog__add-member-heading {
	@include font-and-line-for(1);
	font-weight: 600;
	color: $addMemberHeadingColor;
	margin-bottom: 1rem;
}

.new-team-dialog__add-member-row {
	display: flex;
	align-items: flex-start;
}

.new-team-dialog__add-member-input {
	flex-grow: 1;
	margin-right: .5rem;
}

.new-team-dialog__members-row {
	margin-top: 2rem;
	margin-bottom: .5rem;
}

.new-team-dialog__add-member-button {
	margin-top: .2rem;
}

.new-team-dialog__add-member-btn-spacer {
	@include font-and-line-for(0);
	font-weight: 600;
}
</style>
